import { db } from '@/db/client';
import { getSupabase } from '@/lib/supabase';
import type { PlayerGroup } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { encodePlayerGroupToRow } from '../codec';
import { getUserSyncState, setUserLastUploadedAt } from '../state';

export interface UploadPlayerGroupsStats {
  uploaded: number;
}

export async function uploadPendingPlayerGroups(userId: UserId): Promise<UploadPlayerGroupsStats> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync player groups');
  }

  const { lastUploadedAt = 0 } = await getUserSyncState(userId, 'player_groups');
  const pending: PlayerGroup[] = await db.playerGroups
    .filter((group) => {
      if (group.deletedAt) {
        return group.deletedAt > lastUploadedAt;
      }
      return group.createdAt > lastUploadedAt || group.updatedAt > lastUploadedAt;
    })
    .toArray();

  if (pending.length === 0) return { uploaded: 0 };

  const rows = pending.map((group) => ({
    ...encodePlayerGroupToRow(group),
    user_id: userId,
  }));

  const { error } = await client.from('player_groups').upsert(rows, { onConflict: 'id' });
  if (error) throw error;

  const maxTs = Math.max(
    ...pending.map((group) => Math.max(group.createdAt, group.updatedAt, group.deletedAt ?? 0)),
    0,
  );
  if (maxTs > lastUploadedAt) {
    await setUserLastUploadedAt(userId, 'player_groups', maxTs);
  }

  return { uploaded: pending.length };
}
